/**
 * Repository-managed public project catalog.
 * Only visibility-gated records are exposed to public routes.
 */

import beneath from "./beneath-the-broken-pieces.json";
import theMarket from "./the-market.json";

const ALL_PROJECTS = [beneath, theMarket];

export const PROJECT_STATUS_ORDER = [
  "In Development",
  "Pre-Production",
  "Production",
  "Post-Production",
  "Completed",
  "Released",
  "Coming Soon",
];

export const SITE_ORIGIN = "https://soknoproductions.com";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/sokno-logo.png`;

function isPublicListable(project) {
  return project?.visibility === "published";
}

function isPublicReadable(project) {
  return project?.visibility === "published" || project?.visibility === "unlisted";
}

export function listAllProjects() {
  return ALL_PROJECTS.slice();
}

export function listPublishedProjects() {
  return ALL_PROJECTS.filter(isPublicListable);
}

export function listFeaturedProjects() {
  return listPublishedProjects().filter((p) => p.featured);
}

export function getProjectBySlug(slug) {
  if (!slug) return null;
  const project = ALL_PROJECTS.find((p) => p.slug === slug);
  if (!project || !isPublicReadable(project)) return null;
  return project;
}

export function getRelatedProjects(project, limit = 3) {
  if (!project) return [];
  const bySlug = new Map(listPublishedProjects().map((p) => [p.slug, p]));
  const related = (project.relatedSlugs || [])
    .map((s) => bySlug.get(s))
    .filter(Boolean)
    .filter((p) => p.slug !== project.slug);
  if (related.length >= limit) return related.slice(0, limit);
  const filler = listPublishedProjects().filter(
    (p) => p.slug !== project.slug && !related.some((r) => r.slug === p.slug),
  );
  return [...related, ...filler].slice(0, limit);
}

export function projectPath(slug) {
  return `/projects/${slug}`;
}

export function projectCanonicalUrl(slug) {
  return `${SITE_ORIGIN}${projectPath(slug)}`;
}

export function projectCardCopy(project) {
  return (
    project.shortDescription ||
    project.logline ||
    project.synopsis ||
    "More details coming soon."
  );
}

export function projectDetailCopy(project) {
  if (project.synopsis) return project.synopsis;
  if (project.logline) return project.logline;
  if (project.shortDescription) return project.shortDescription;
  return "More details coming soon.";
}

export function projectOgImage(project) {
  return project?.ogImage || project?.poster || project?.heroImage || DEFAULT_OG_IMAGE;
}

export function filterProjects(projects, { status, format } = {}) {
  return projects.filter((p) => {
    if (status && status !== "all" && p.status !== status) return false;
    if (format && format !== "all" && p.format !== format) return false;
    return true;
  });
}

export function uniqueStatuses(projects = listPublishedProjects()) {
  const set = new Set(projects.map((p) => p.status).filter(Boolean));
  return PROJECT_STATUS_ORDER.filter((s) => set.has(s)).concat(
    [...set].filter((s) => !PROJECT_STATUS_ORDER.includes(s)),
  );
}

export function uniqueFormats(projects = listPublishedProjects()) {
  return [...new Set(projects.map((p) => p.format).filter(Boolean))];
}
